'use es6';

import { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import localeComparator from 'I18n/utils/localeComparator';
import UILink from 'UIComponents/link/UILink';
import UIDescriptionList from 'UIComponents/list/UIDescriptionList';
import UITag from 'UIComponents/tag/UITag';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import * as Category from 'ecosystem-marketplace-core/constants/groupTags/Category';
import { getInstallsBucket } from 'ecosystem-marketplace-core/utils/offerings';
import { getCategoryFeatureI18nKey } from 'marketplace-ui-apps-core/common/utils/i18n';
import { getCategoryI18nKey } from 'marketplace-ui-common/utils/i18n';
import { CATEGORY_FEATURES } from '../../../../constants/search/FilterKey';
import { reportMissingCategoryFeatureTranslation } from '../../../../utils/sentry';
import { getFormattedUrl } from '../../../../utils/urls';
import FilterTag from '../FilterTag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const I18N_PATH = 'appMarketplace.detail.overview.sidebar.details';
const InstallsBucket = ({
  value
}) => {
  const bucket = getInstallsBucket(value);
  if (!bucket) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${I18N_PATH}.installsBuckets.min`
    });
  }
  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `${I18N_PATH}.installsBuckets.bucket_jsx`,
    options: {
      value: bucket
    },
    elements: {
      Number: FormattedNumber
    }
  });
};
const Section = ({
  children,
  label,
  labelClassName
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx("dt", {
    className: labelClassName,
    children: label
  }), /*#__PURE__*/_jsx("dd", {
    children: children
  })]
});
const AppDetails = ({
  appLanguages,
  category,
  companyName,
  companyUrl,
  categoryFeatures,
  getFilterLink,
  homemade,
  installs,
  isPreview,
  onTrackInteraction
}) => {
  const trackInteraction = useCallback((action, value) => onTrackInteraction(action, 'details', value), [onTrackInteraction]);
  const renderedCategorySection = useMemo(() => {
    return category ? /*#__PURE__*/_jsx(Section, {
      label: unescapedText(`${I18N_PATH}.categoryLabel`),
      labelClassName: "m-bottom-1",
      children: /*#__PURE__*/_jsx(UIRouterLink, {
        disabled: isPreview,
        to: getFilterLink(),
        children: /*#__PURE__*/_jsx(FilterTag, {
          children: /*#__PURE__*/_jsx(UITag, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getCategoryI18nKey(category)
            })
          })
        })
      })
    }) : null;
  }, [category, getFilterLink, isPreview]);
  const renderedFeaturesSection = useMemo(() => {
    return category && categoryFeatures.length ? /*#__PURE__*/_jsx(Section, {
      label: unescapedText(`${I18N_PATH}.featuresLabel`),
      labelClassName: "m-bottom-1",
      children: categoryFeatures.map(feature => [feature, getCategoryFeatureI18nKey(category, feature)]).filter(([feature, featureI18nKey]) => {
        if (!isValidI18nKey(featureI18nKey)) {
          reportMissingCategoryFeatureTranslation(category, feature, {
            component: 'AppDetail'
          });
          return false;
        }
        return true;
      }).sort(([__, featureAI18nKey], [___, featureBI18nKey]) => localeComparator(unescapedText(featureBI18nKey), unescapedText(featureAI18nKey))).map(([feature, featureI18nKey]) => /*#__PURE__*/_jsx(UIRouterLink, {
        disabled: isPreview,
        to: getFilterLink(CATEGORY_FEATURES, feature),
        children: /*#__PURE__*/_jsx(FilterTag, {
          children: /*#__PURE__*/_jsx(UITag, {
            className: "m-left-0 m-right-1",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: featureI18nKey
            })
          })
        })
      }, feature))
    }) : null;
  }, [category, categoryFeatures, getFilterLink, isPreview]);
  const renderedLanguagesSection = useMemo(() => {
    if (appLanguages.size === 0 || appLanguages.some(lang => !lang)) {
      return null;
    }
    return /*#__PURE__*/_jsx(Section, {
      label: homemade ? unescapedText(`${I18N_PATH}.homemadeAppLanguagesLabel`) : unescapedText(`${I18N_PATH}.appLanguagesLabel`),
      labelClassName: "m-bottom-1",
      children: /*#__PURE__*/_jsx(FormattedList, {
        children: appLanguages.map(languageCode => [languageCode, `SharedI18nStrings.languageNames.${languageCode}`]).sort(([__, languageNameI18nKey]) => unescapedText(languageNameI18nKey)).map(([languageCode, languageNameI18nKey]) => /*#__PURE__*/_jsx(FormattedMessage, {
          message: languageNameI18nKey
        }, languageCode))
      })
    });
  }, [appLanguages, homemade]);
  const renderedCompanyName = useMemo(() => {
    return companyUrl ? /*#__PURE__*/_jsx(UILink, {
      external: true,
      href: getFormattedUrl(companyUrl),
      onClick: () => trackInteraction('click provider link'),
      children: companyName
    }) : companyName;
  }, [companyName, companyUrl, trackInteraction]);
  const renderedInstallsSection = useMemo(() => {
    return /*#__PURE__*/_jsx(Section, {
      label: unescapedText(`${I18N_PATH}.installsLabel`),
      children: /*#__PURE__*/_jsx(InstallsBucket, {
        value: installs
      })
    });
  }, [installs]);
  return /*#__PURE__*/_jsxs(UIDescriptionList, {
    "data-test-id": "app-details",
    children: [/*#__PURE__*/_jsx(Section, {
      label: unescapedText(`${I18N_PATH}.providerLabel`),
      children: renderedCompanyName
    }), renderedInstallsSection, renderedCategorySection, renderedFeaturesSection, renderedLanguagesSection]
  });
};
AppDetails.propTypes = {
  appLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  category: PropTypes.oneOf(Object.values(Category)),
  categoryFeatures: PropTypes.array.isRequired,
  companyName: PropTypes.string.isRequired,
  companyUrl: PropTypes.string,
  getFilterLink: PropTypes.func.isRequired,
  homemade: PropTypes.bool.isRequired,
  installs: PropTypes.number.isRequired,
  isPreview: PropTypes.bool.isRequired,
  onTrackInteraction: PropTypes.func.isRequired
};
export default AppDetails;